import type {
  AgentTool,
  AppointmentStatus,
  CallStatus,
  ChannelKind,
  ConnectionProvider,
  ConnectionStatus,
  ConversationMode,
  DeliveryStatus,
  DeploymentStatus,
  DocumentStatus,
  InventoryAuthority,
  ProjectStatus,
  ReceiptStatus,
  Role,
  RoutineState,
  SetupStepKey,
  SetupStepStatus,
  UnitAvailability,
  VersionState,
  Weekday,
  WebhookDeliveryStatus,
} from './enums.js';

// Spanish (Mexico) interface labels. Keep wording operational and concise.

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Propietario',
  admin: 'Administrador',
  manager: 'Gerente',
  advisor: 'Asesor',
  viewer: 'Lector',
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: 'Gestiona propiedad del espacio y configuración comercial.',
  admin: 'Configura integraciones, conocimiento, instrucciones y usuarios.',
  manager: 'Gestiona asignación operativa, campañas y reportes.',
  advisor: 'Atiende contactos, conversaciones y citas permitidas.',
  viewer: 'Consulta reportes permitidos.',
};

export const SETUP_STEP_LABELS: Record<SetupStepKey, { title: string; description: string }> = {
  workspace_identity: { title: 'Identidad de la desarrolladora', description: 'Zona horaria, idioma, contacto de soporte y datos del negocio.' },
  first_project: { title: 'Primer proyecto', description: 'Ubicación, argumentos de venta, lugar de visita, equipo y afirmaciones permitidas.' },
  channels: { title: 'Canales', description: 'Conecta WhatsApp, Aircall, ambos o ninguno. Cada conexión es independiente.' },
  routing: { title: 'Enrutamiento de números', description: 'Número compartido o por proyecto; asigna cada línea a sus proyectos.' },
  knowledge_inventory: { title: 'Conocimiento e inventario', description: 'Documentos, material compartible y fuente de inventario con una unidad de prueba.' },
  agent_schedule: { title: 'Agente y horario', description: 'Calificación, escalamiento, instrucciones y horario de operación por canal.' },
  crm_calendar: { title: 'CRM y Calendly', description: 'Conecta el CRM y Calendly; mapea proyectos, asesores y tipos de evento.' },
  first_routine: { title: 'Primera automatización', description: 'Crea una rutina desde plantilla y revisa su vista previa.' },
  test_run: { title: 'Prueba', description: 'Simulación y luego prueba real con número y prospecto de prueba permitidos.' },
  activation: { title: 'Activación', description: 'Activa proyectos y rutinas cuando sus requisitos estén listos.' },
};

export const SETUP_STATUS_LABELS: Record<SetupStepStatus, string> = {
  pending: 'Pendiente',
  in_progress: 'En progreso',
  ready: 'Listo',
  skipped: 'Omitido',
  needs_attention: 'Requiere atención',
};

export const PROVIDER_LABELS: Record<ConnectionProvider, string> = {
  kapso_whatsapp: 'WhatsApp (Kapso)',
  meta_whatsapp: 'WhatsApp (Meta)',
  aircall: 'Aircall',
  calendly: 'Calendly',
  calcom: 'Cal.com',
  lia_test: 'Prueba en vivo',
  hubspot: 'HubSpot',
  highlevel: 'HighLevel',
  generic_webhook: 'Webhook genérico',
  telnyx: 'Telnyx',
};

export const CONNECTION_STATUS_LABELS: Record<ConnectionStatus, string> = {
  pending: 'Pendiente de validación',
  connected: 'Conectado',
  needs_attention: 'Requiere atención',
  disconnected: 'Desconectado',
  revoked: 'Credenciales revocadas',
};

export const CONVERSATION_MODE_LABELS: Record<ConversationMode, string> = {
  ai_active: 'IA activa',
  human_owned: 'Atiende un asesor',
  paused: 'IA en pausa',
  closed: 'Cerrada',
};

export const DELIVERY_STATUS_LABELS: Record<DeliveryStatus, string> = {
  queued: 'En cola',
  provider_accepted: 'Aceptado por el proveedor',
  delivered: 'Entregado',
  read: 'Leído',
  failed: 'Falló',
  unknown: 'Desconocido',
};

export const CALL_STATUS_LABELS: Record<CallStatus, string> = {
  queued: 'En cola',
  provider_accepted: 'Aceptada por el proveedor',
  ringing: 'Timbrando',
  answered: 'Contestada',
  completed: 'Finalizada',
  no_answer: 'Sin respuesta',
  busy: 'Ocupado',
  voicemail: 'Buzón de voz',
  failed: 'Falló',
  uncertain: 'Resultado incierto',
};

export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  proposed: 'Propuesta',
  booking: 'Agendando',
  confirmed: 'Confirmada',
  reschedule_requested: 'Reprogramación solicitada',
  cancel_requested: 'Cancelación solicitada',
  cancelled: 'Cancelada',
  completed: 'Realizada',
  no_show: 'No asistió',
  failed: 'Falló',
  reconciliation_required: 'Requiere conciliación',
};

export const UNIT_AVAILABILITY_LABELS: Record<UnitAvailability, string> = {
  available: 'Disponible',
  held: 'Apartada (temporal)',
  reserved: 'Reservada',
  sold: 'Vendida',
  unavailable: 'No disponible',
  unknown: 'Desconocido',
};

export const INVENTORY_AUTHORITY_LABELS: Record<InventoryAuthority, string> = {
  crm: 'El CRM es la fuente oficial',
  app: 'Esta aplicación es la fuente oficial',
  mixed: 'Campos repartidos entre CRM y aplicación',
};

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  uploaded: 'Cargado',
  processing: 'Procesando',
  needs_review: 'Requiere revisión',
  published: 'Publicado',
  failed: 'Falló',
  superseded: 'Reemplazado',
};

export const VERSION_STATE_LABELS: Record<VersionState, string> = {
  draft: 'Borrador',
  tested: 'Probado',
  published: 'Publicado',
  archived: 'Archivado',
};

export const DEPLOYMENT_STATUS_LABELS: Record<DeploymentStatus, string> = {
  pending: 'Pendiente',
  applied: 'Aplicado',
  failed: 'Falló',
  action_required: 'Acción requerida en el proveedor',
};

export const ROUTINE_STATE_LABELS: Record<RoutineState, string> = {
  draft: 'Borrador',
  test: 'Prueba',
  live: 'Activa',
  paused: 'Pausada',
};

export const RECEIPT_STATUS_LABELS: Record<ReceiptStatus, string> = {
  accepted: 'Aceptado',
  processing: 'Procesando',
  applied: 'Aplicado',
  skipped: 'Omitido',
  failed: 'Falló',
  quarantined: 'En cuarentena',
};

export const WEBHOOK_DELIVERY_STATUS_LABELS: Record<WebhookDeliveryStatus, string> = {
  pending: 'Pendiente',
  delivering: 'Enviando',
  succeeded: 'Entregado',
  failed: 'Reintentando',
  dead_letter: 'Descartado tras reintentos',
};

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  draft: 'Borrador',
  active: 'Activo',
  paused: 'En pausa',
  archived: 'Archivado',
};

export const CHANNEL_LABELS: Record<ChannelKind, string> = {
  whatsapp: 'WhatsApp',
  voice: 'Llamadas',
};

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: 'Lunes',
  tue: 'Martes',
  wed: 'Miércoles',
  thu: 'Jueves',
  fri: 'Viernes',
  sat: 'Sábado',
  sun: 'Domingo',
};

export const TOOL_LABELS: Record<AgentTool, string> = {
  get_project_context: 'Contexto del proyecto',
  search_knowledge: 'Buscar en conocimiento',
  list_matching_units: 'Buscar unidades',
  get_unit: 'Detalle de unidad',
  get_current_price: 'Precio vigente',
  get_availability: 'Disponibilidad de citas',
  book_appointment: 'Agendar cita',
  request_reschedule: 'Solicitar reprogramación',
  request_cancel: 'Solicitar cancelación',
  send_approved_asset: 'Enviar material aprobado',
  update_qualification: 'Actualizar calificación',
  request_handoff: 'Transferir a asesor',
  record_opt_out: 'Registrar baja',
  record_language: 'Registrar idioma preferido',
  report_abuse: 'Reportar posible abuso',
  schedule_commitment: 'Programar llamada pactada',
  record_objection: 'Registrar objeción',
  confirm_attendance: 'Registrar confirmación de asistencia',
  record_pending_question: 'Registrar pregunta por resolver',
};
